import {DateUtilsTypes} from '../../../../core/services/date-utils';
import {CommitDateRangeArg, DateRangeFormMode, DateRangeInputMode} from './types';
import {DateRangeUtils} from './DateRangeUtils';

/**
 * A lightweight data-model class that wraps a CommitDateRangeArg snapshot
 * and exposes display/resolution methods via DateRangeUtils.
 */
export class DateRange {
    constructor(
        private readonly value: CommitDateRangeArg,
        private readonly inputMode?: DateRangeInputMode,
    ) {
    }

    /** The active form mode */
    get mode(): DateRangeFormMode {
        return this.value.mode ?? 'auto';
    }

    /**
     * Returns a human-readable label for the current value,
     * or undefined if the value is incomplete.
     */
    display(): string | undefined {
        return DateRangeUtils.displayDateRange(this.value, this.inputMode);
    }

    /**
     * Resolves the current value to a concrete [start, end] date pair,
     * or undefined if the value is incomplete.
     */
    getDateRange(): [DateUtilsTypes.Instance | undefined, DateUtilsTypes.Instance | undefined] | undefined {
        return DateRangeUtils.getDateRange(this.value, this.inputMode);
    }

    /** Returns a plain JS object copy suitable for serialization */
    toJSON(): CommitDateRangeArg {
        return {...this.value};
    }

    /** Factory: creates a DateRange from a CommitDateRangeArg */
    static from(value: CommitDateRangeArg, inputMode?: DateRangeInputMode): DateRange {
        return new DateRange(value, inputMode);
    }

    /** Factory: creates an empty (unresolved) DateRange */
    static empty(inputMode?: DateRangeInputMode): DateRange {
        return new DateRange({}, inputMode);
    }
}
